import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, MoreVertical, CheckCircle, FileText, Building2, Calendar, DollarSign } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Table, Pagination, Button, Badge,
    SearchInput, Select, Dropdown, DropdownItem,
    ConfirmDialog, Modal, toast
} from '../../components/common';
import { invoiceApi } from '../../services/api';
import './Invoices.css';

export default function InvoiceList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [actionInvoice, setActionInvoice] = useState(null);
    const [actionType, setActionType] = useState('');
    const [viewingInvoice, setViewingInvoice] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ['invoices', page, pageSize, search, statusFilter],
        queryFn: () => invoiceApi.list({ page, limit: pageSize, search, status: statusFilter }),
        select: (res) => res.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ id, action }) => {
            switch (action) {
                case 'verify': return invoiceApi.verify(id);
                case 'approve': return invoiceApi.approve(id);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            toast.success(`Invoice ${action}d successfully`);
            queryClient.invalidateQueries(['invoices']);
            setActionInvoice(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getStatusBadge = (status) => {
        const config = {
            'draft': 'default',
            'pending_verification': 'warning',
            'verified': 'info',
            'approved': 'success',
            'paid': 'success',
            'rejected': 'danger'
        };
        return <Badge variant={config[status] || 'default'}>{status?.replace('_', ' ')}</Badge>;
    };

    const columns = [
        {
            key: 'invoiceNumber',
            title: 'Invoice Number',
            dataIndex: 'invoiceNumber',
            render: (num) => <span className="invoice-number">{num}</span>
        },
        {
            key: 'vendor',
            title: 'Vendor',
            dataIndex: 'vendor',
            render: (vendor) => <span>{vendor?.name || '-'}</span>
        },
        {
            key: 'amount',
            title: 'Amount',
            dataIndex: 'totalAmount',
            align: 'right',
            render: (val) => <span className="invoice-amount">{formatCurrency(val)}</span>
        },
        {
            key: 'dueDate',
            title: 'Due Date',
            dataIndex: 'dueDate',
            render: (date) => formatDate(date)
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); setViewingInvoice(row); }}>
                                View Details
                            </DropdownItem>
                            {row.status === 'pending_verification' && (
                                <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionInvoice(row); setActionType('verify'); }}>
                                    Verify
                                </DropdownItem>
                            )}
                            {row.status === 'verified' && (
                                <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionInvoice(row); setActionType('approve'); }}>
                                    Approve
                                </DropdownItem>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending_verification', label: 'Pending Verification' },
        { value: 'verified', label: 'Verified' },
        { value: 'approved', label: 'Approved' },
        { value: 'paid', label: 'Paid' }
    ];

    return (
        <div className="invoice-page">
            <PageHeader
                title="Invoices"
                subtitle="Manage vendor invoices"
                breadcrumbs={[{ label: 'Invoices', path: '/invoices' }]}
                actions={
                    <Button icon={Plus} onClick={() => navigate('/invoices/create')}>
                        Create Invoice
                    </Button>
                }
            />

            <Card>
                <div className="invoice-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search invoices..."
                        className="invoice-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        className="invoice-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No invoices found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="invoice-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={!!actionInvoice}
                onClose={() => { setActionInvoice(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionInvoice?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Invoice`}
                message={`Are you sure you want to ${actionType} this invoice?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                loading={actionMutation.isPending}
            />

            {/* Invoice Detail Modal */}
            <Modal
                isOpen={!!viewingInvoice}
                onClose={() => setViewingInvoice(null)}
                title="Invoice Details"
                size="lg"
            >
                {viewingInvoice && (
                    <div className="invoice-detail-modal">
                        <div className="invoice-modal-header">
                            <div>
                                <h3>{viewingInvoice.invoiceNumber}</h3>
                                <span className="vendor-invoice">{viewingInvoice.vendorInvoiceNumber}</span>
                            </div>
                            {getStatusBadge(viewingInvoice.status)}
                        </div>
                        <div className="invoice-modal-grid">
                            <div className="detail-item">
                                <Building2 size={16} />
                                <div>
                                    <label>Vendor</label>
                                    <span>{viewingInvoice.vendor?.name || '-'}</span>
                                </div>
                            </div>
                            <div className="detail-item">
                                <DollarSign size={16} />
                                <div>
                                    <label>Total Amount</label>
                                    <span>{formatCurrency(viewingInvoice.totalAmount)}</span>
                                </div>
                            </div>
                            <div className="detail-item">
                                <Calendar size={16} />
                                <div>
                                    <label>Invoice Date</label>
                                    <span>{formatDate(viewingInvoice.invoiceDate)}</span>
                                </div>
                            </div>
                            <div className="detail-item">
                                <Calendar size={16} />
                                <div>
                                    <label>Due Date</label>
                                    <span>{formatDate(viewingInvoice.dueDate)}</span>
                                </div>
                            </div>
                            <div className="detail-item">
                                <FileText size={16} />
                                <div>
                                    <label>PO Number</label>
                                    <span>{viewingInvoice.purchaseOrder?.poNumber || '-'}</span>
                                </div>
                            </div>
                        </div>
                        {viewingInvoice.notes && (
                            <div className="invoice-notes">
                                <label>Notes</label>
                                <p>{viewingInvoice.notes}</p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}
